import React, { useState, useEffect } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  useDataProvider,
  useRedirect,
  useRefresh,
  CreateProps,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, Typography, makeStyles } from "@material-ui/core";
import { GetParam } from "utils/tools";
import CustomCreateToolbar from "components/CustomCreateToolbar";
import SubcontractorRateChartAreaDatagrid from "./components/SubcontractorRateChartAreaDatagrid";
import CapacityIncentiveRateChartAreaDatagrid from "./components/CapacityIncentiveRateChartAreaDatagrid";
import SubcontractorRateChartAreaDatagridSchoolIncentive from "./components/SubcontractorRateChartAreaDatagridSchoolIncentive";
import CustomNumberInput from "../../components/CustomNumberInput";
import CreateWrapper from "../../components/CreateWrapper";

const useStyles = makeStyles({
  additionalIncentive: {
    marginLeft: "8px",
  },
});

const SubcontractorRateChartsCreate = (props: CreateProps) => {
  const [areas, setAreas] = useState<ISubcontractorAreas[]>([]);

  const provider = useDataProvider();
  const param = GetParam();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const breadcrumb = [
    { title: "Schools", link: `/schools` },
    {
      title: "Subcontractor Pricing",
      link: `/schools/${param.get("schoolId")}/show/6`,
    },
    { title: "Create", link: "" },
  ];

  const classes = useStyles();

  //fetch area data for current school on first load
  useEffect(() => {
    provider
      .getList("subcontractorAreas", {
        filter: { SchoolId: Number(param.get("schoolId")) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "MaxDistanceInMeters",
          order: "ASC",
        },
      })
      .then((result: any) => {
        result.data
          .sort((a: any, b: any) => {
            return a.maxDistanceInMeters - b.maxDistanceInMeters;
          })
          .map((record: any, index: number) => {
            const item = record;
            if (item) {
              item.order = index + 1;
            }
          });

        setAreas(result.data);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const transform = (data: any) => {
    data.items = [...data.regularItemsToSend, ...data.ccaItemsToSend];
    data.capacityIncentiveRates = [...data.capacityIncentiveRatesToSend];
    data.schoolIncentives = [...data.schoolIncentivesToSend];
    data.busAssistantIncentive = Math.round(
      Number(data.busAssistantIncentive) * 100.0
    );

    data.additionalIncentiveRates.smallVehicleFee = Math.round(
      Number(data.additionalIncentiveRates.smallVehicleFee) * 100.0
    );
    data.additionalIncentiveRates.mediumVehicleFee = Math.round(
      Number(data.additionalIncentiveRates.mediumVehicleFee) * 100.0
    );

    delete data.regularItemsToSend;
    delete data.ccaItemsToSend;
    delete data.capacityIncentiveRatesToSend;
    delete data.schoolIncentivesToSend;

    return data;
  };

  const CustomRedirect = (props: { data: ISubcontractorRateCharts }) => {
    redirect(`/schools/${props.data.schoolId}/show/6`);
    refresh();
  };

  return (
    <>
      <CreateWrapper
        breadcrumb={breadcrumb}
        headerTitle="New Subcontractor Rate Chart"
        transform={transform}
        onSuccess={CustomRedirect}
        {...props}
      >
        <SimpleForm toolbar={<CustomCreateToolbar />}>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School</Typography>
            </Grid>

            <Grid item xs={3}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                validate={[required()]}
                disabled
              >
                <AutocompleteInput optionText="name" optionId="id" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Pricing Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                source="name"
                label="Pricing Name"
                validate={required()}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <CustomNumberInput
                source="busAssistantIncentive"
                label="Bus Assistant Incentive ($)"
                isRequired
                isPositiveValue
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Area Pricing for Regular</Typography>
            </Grid>

            <Grid item xs={6}>
              <SubcontractorRateChartAreaDatagrid
                areas={areas}
                type="regular"
                itemsToSendField="regularItemsToSend"
              />
            </Grid>
            <Grid item xs={6}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Area Pricing for CCA</Typography>
            </Grid>

            <Grid item xs={6}>
              <SubcontractorRateChartAreaDatagrid
                areas={areas}
                type="cca"
                itemsToSendField="ccaItemsToSend"
              />
            </Grid>
            <Grid item xs={6}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Incentive Threshold</Typography>
            </Grid>

            <Grid container item>
              <Grid item xs={12}>
                <Typography variant="body2">Additional Incentive</Typography>
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="smallVehicleAdditionalIncentiveThreshold"
                  label="Small Vehicle (student)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="mediumVehicleAdditionalIncentiveThreshold"
                  label="Medium Vehicle (student)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
            </Grid>

            <Grid container item>
              <Grid item xs={12}>
                <Typography variant="body2">Capacity Incentive</Typography>
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="mediumVehicleCapacityIncentiveThreshold"
                  label="Medium Vehicle (student)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="largeVehicleCapacityIncentiveThreshold"
                  label="Large Vehicle (student)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Additional Incentive Rates</Typography>
            </Grid>

            <Grid container item>
              <Grid item xs={12}>
                <Typography variant="body2">Small Vehicle</Typography>
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="additionalIncentiveRates.smallVehicleFee"
                  label="Fee ($)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
            </Grid>
            <Grid container item>
              <Grid item xs={12}>
                <Typography variant="body2">Medium Vehicle</Typography>
              </Grid>
              <Grid item xs={3} className={classes.additionalIncentive}>
                <CustomNumberInput
                  source="additionalIncentiveRates.mediumVehicleFee"
                  label="Fee ($)"
                  isRequired
                  isPositiveValue
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Capacity Incentive Rates</Typography>
            </Grid>

            <Grid item xs={6}>
              <CapacityIncentiveRateChartAreaDatagrid areas={areas} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">School Incentive Pricing</Typography>
            </Grid>

            <Grid item xs={6}>
              <SubcontractorRateChartAreaDatagridSchoolIncentive
                areas={areas}
              />
            </Grid>
            <Grid item xs={6}></Grid>
          </SanitizedGrid>
        </SimpleForm>
      </CreateWrapper>
    </>
  );
};

export default SubcontractorRateChartsCreate;
